import {
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    type Ref
} from 'react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
    FindingRefDecoration,
    CLASS_MOD_HELD,
    CLASS_REF,
    CLASS_REF_FIRST,
    CLASS_REF_LAST,
    CLASS_REF_LINKED,
    CLASS_REF_NUMBER,
    CLASS_REF_OVERALL
} from './FindingRefDecoration';
import type { FindingRefRange } from './findingRefRanges';
import {
    CLASS_FRESH,
    FreshTextDecoration,
    type FreshRange
} from './FreshTextDecoration';
import { offsetForPos, posForOffset } from './textOffsets';

/**
 * A PLAIN-TEXT editor that marks the Befunde its text names.
 *
 * A rich-text engine used for something that is not rich text: the value going
 * in and out is a string, because the Tagebuch and the descriptions round-trip
 * through the ExcaBook as plain text. Every formatting node and mark StarterKit
 * brings is therefore switched OFF.
 *
 * The chips are decorations (see FindingRefDecoration): they paint over the
 * document without changing it, so a reference stays ordinary text that the
 * caret can enter and the serialiser cannot mangle.
 */

/**
 * What a parent needs to drive the editor.
 *
 * Everything here counts in CHARACTERS of the plain text, not in document
 * positions, so a caller can keep doing its arithmetic on the string it already
 * has. The translation happens in textOffsets.
 */
export interface RefTextEditorHandle {
    /** Caret as a character offset into the plain text. */
    getCaret: () => number;
    /** Select a range of the plain text, e.g. a phrase's `[…]` placeholder. */
    selectRange: (start: number, end: number) => void;
    focus: () => void;
}

/**
 * The chip's corner radius, and why it is not MUI's own 12px.
 *
 * These pieces are INLINE spans: their box is the font's content area plus 1px
 * of padding and the border — about 19px. Two 12px radii on one side then add
 * up to more than the box is tall, and CSS resolves that by scaling EVERY
 * radius of the box down by a factor that depends on the box — so a short chip
 * and a long one came out rounded differently. Kept under half the box height,
 * the browser never rescales.
 */
const CHIP_RADIUS = '9px';
/**
 * A Gesamtbeschreibung's chip: the same outline, only nearly square, so it
 * reads apart from a Befund at a glance.
 */
const OVERALL_CHIP_RADIUS = '3px';

/** Plain text as a document: one paragraph per line, which is what `\n` means. */
function toDoc(text: string) {
    return {
        type: 'doc',
        content: text.split('\n').map((line) => ({
            type: 'paragraph',
            // An empty paragraph must have NO content array with an empty text
            // node in it — an empty text node is not a valid ProseMirror node.
            ...(line ? { content: [{ type: 'text', text: line }] } : {})
        }))
    };
}

export default function RefTextEditor({
    value,
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    readOnly = false,
    minRows = 3,
    fullscreen = false,
    ariaLabel,
    resolves,
    onOpen,
    titleFor,
    handleRef,
    freshRange = null,
    locked = false
}: {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    disabled?: boolean;
    /** Not editable, but drawn in full strength and without the tint. */
    readOnly?: boolean;
    minRows?: number;
    /** Inside the fullscreen dialog the editing area grows and scrolls itself. */
    fullscreen?: boolean;
    /** `InputBase` sets this for its textarea; a contenteditable needs it given. */
    ariaLabel?: string;
    /** Whether a reference points at a record that exists — see the extension. */
    resolves?: (range: FindingRefRange) => boolean;
    onOpen?: (range: FindingRefRange) => void;
    /** Native tooltip for a linked reference — where Ctrl+click is announced. */
    titleFor?: (range: FindingRefRange) => string;
    handleRef?: Ref<RefTextEditorHandle>;
    /** Text that has just arrived (a dictation), tinted — see FreshTextDecoration. */
    freshRange?: FreshRange | null;
    /**
     * Not editable for the moment, but drawn as an editable field — while a
     * dictation writes into it, typing would fight the text DeepL revises.
     */
    locked?: boolean;
}) {
    const extensions = useMemo(
        () => [
            StarterKit.configure({
                // Everything that could put MARKUP into a plain-text value.
                bold: false,
                italic: false,
                strike: false,
                code: false,
                codeBlock: false,
                blockquote: false,
                bulletList: false,
                orderedList: false,
                listItem: false,
                heading: false,
                horizontalRule: false,
                link: false,
                underline: false
            }),
            Placeholder.configure({ placeholder: placeholder ?? '' }),
            // No callbacks here — they go into the extension's storage below,
            // because this list is built ONCE: rebuilding it recreates the
            // editor and drops the caret mid-word.
            FindingRefDecoration,
            FreshTextDecoration
        ],
        [placeholder]
    );

    const editor = useEditor({
        extensions,
        content: toDoc(value),
        editable: !disabled && !readOnly,
        // `getText` with an explicit separator: the default joins blocks with
        // "\n\n", which would double every line break on the way back out.
        onUpdate: ({ editor }) =>
            onChange(editor.getText({ blockSeparator: '\n' })),
        onBlur: () => onBlur?.(),
        /**
         * A contenteditable is not a textarea: without these it reaches the
         * accessibility tree as an unlabelled group.
         */
        editorProps: {
            attributes: {
                role: 'textbox',
                'aria-multiline': 'true',
                // A contenteditable gets no sentence autocapitalization from
                // the phone keyboard unless it is asked for explicitly.
                autocapitalize: 'sentences',
                ...(ariaLabel ? { 'aria-label': ariaLabel } : {})
            }
        }
    });

    /**
     * Take an outside change without echoing it back: `setContent` would fire
     * onUpdate and re-report what we were just handed.
     */
    useEffect(() => {
        if (!editor) {
            return;
        }
        if (value !== editor.getText({ blockSeparator: '\n' })) {
            editor.commands.setContent(toDoc(value), { emitUpdate: false });
        }
    }, [editor, value]);

    useEffect(() => {
        // Without an update: the text did not change, and an update would be
        // reported as an edit (a translation would turn "überarbeitet").
        editor?.setEditable(!disabled && !readOnly && !locked, false);
    }, [editor, disabled, readOnly, locked]);

    useEffect(() => {
        editor?.commands.setFreshRange(freshRange);
    }, [editor, freshRange]);

    /**
     * Hand the CURRENT callbacks to the plugin and redraw. A new `resolves`
     * means the records behind the references changed — a Befund finished
     * loading, or somebody created one in another tab — and the chips have to
     * follow although nobody typed.
     */
    useEffect(() => {
        if (!editor) {
            return;
        }
        editor.commands.setFindingRefHandlers({
            resolves: resolves ?? null,
            onOpen: onOpen ?? null,
            titleFor: titleFor ?? null
        });
    }, [editor, resolves, onOpen, titleFor]);

    /**
     * Track Ctrl/Cmd so the chip can SAY when it is a link.
     *
     * The class is toggled on the DOM node rather than held in state: this fires
     * on every press of a modifier key. `blur` clears it because a key-up that
     * happens in another window never reaches us, and the chip would otherwise
     * stay a hand for good.
     */
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const held = (event: KeyboardEvent) => event.ctrlKey || event.metaKey;
        const set = (on: boolean) =>
            containerRef.current?.classList.toggle(CLASS_MOD_HELD, on);
        const onKey = (event: KeyboardEvent) => set(held(event));
        const onBlur = () => set(false);
        window.addEventListener('keydown', onKey);
        window.addEventListener('keyup', onKey);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('keyup', onKey);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    useImperativeHandle(
        handleRef,
        (): RefTextEditorHandle => ({
            getCaret: () =>
                editor
                    ? offsetForPos(
                          editor.state.doc,
                          editor.state.selection.from
                      )
                    : 0,
            selectRange: (start, end) => {
                if (!editor) {
                    return;
                }
                const { doc } = editor.state;
                editor
                    .chain()
                    .focus()
                    .setTextSelection({
                        from: posForOffset(doc, start),
                        to: posForOffset(doc, end)
                    })
                    .run();
            },
            focus: () => editor?.commands.focus()
        }),
        [editor]
    );

    return (
        <Box
            ref={containerRef}
            sx={(theme) => ({
                /**
                 * Matched to the `InputBase` this replaces, so swapping the two
                 * does not move the text: its px/py, its body typography, and
                 * the filled-input tint.
                 */
                px: 1.5,
                py: 1,
                ...theme.typography.body1,
                bgcolor: readOnly
                    ? 'transparent'
                    : theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.06)'
                      : 'rgba(255, 255, 255, 0.09)',
                ...(disabled && { color: theme.palette.text.disabled }),
                ...(fullscreen && { flexGrow: 1, overflow: 'auto' }),
                '& .tiptap': {
                    outline: 'none',
                    minHeight: `${minRows * 1.4375}em`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                },
                '& .tiptap p': { margin: 0 },
                // New text, in the info tone — the same tint as TextareaHighlight.
                [`& .${CLASS_FRESH}`]: {
                    backgroundColor: alpha(theme.palette.info.main, 0.22),
                    borderRadius: '2px'
                },
                // The placeholder — an empty first paragraph shows it.
                '& .tiptap p.is-editor-empty:first-of-type::before': {
                    content: 'attr(data-placeholder)',
                    color: theme.palette.text.disabled,
                    float: 'left',
                    height: 0,
                    pointerEvents: 'none'
                },
                /**
                 * The chip, styled after MUI's own small outlined Chip so it
                 * follows light/dark without a palette of its own.
                 *
                 * Drawn from SEVERAL spans (see FindingRefDecoration): each
                 * piece carries the top and bottom rule, and only the opening
                 * and closing ones carry a side and the radius. That is what
                 * makes the pieces read as one outline instead of one ring per
                 * piece.
                 */
                [`& .${CLASS_REF}`]: {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    paddingTop: '1px',
                    paddingBottom: '1px',
                    // So the chip does not read as a different typeface inside
                    // the sentence.
                    fontSize: theme.typography.pxToRem(16)
                },
                [`& .${CLASS_REF_FIRST}`]: {
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderTopLeftRadius: CHIP_RADIUS,
                    borderBottomLeftRadius: CHIP_RADIUS,
                    paddingLeft: '7px'
                },
                [`& .${CLASS_REF_LAST}`]: {
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderTopRightRadius: CHIP_RADIUS,
                    borderBottomRightRadius: CHIP_RADIUS,
                    paddingRight: '7px'
                },
                [`& .${CLASS_REF_OVERALL}.${CLASS_REF_FIRST}`]: {
                    borderTopLeftRadius: OVERALL_CHIP_RADIUS,
                    borderBottomLeftRadius: OVERALL_CHIP_RADIUS
                },
                [`& .${CLASS_REF_OVERALL}.${CLASS_REF_LAST}`]: {
                    borderTopRightRadius: OVERALL_CHIP_RADIUS,
                    borderBottomRightRadius: OVERALL_CHIP_RADIUS
                },
                [`& .${CLASS_REF_NUMBER}`]: { fontWeight: 700 },
                // The linked state, matching an outlined Chip that is
                // `clickable`: the primary rule and its hover tint.
                [`& .${CLASS_REF_LINKED}`]: {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.action.hoverOpacity
                    )
                },
                // Only while the modifier is down is a plain click a link, so
                // only then does the chip offer a hand.
                [`&.${CLASS_MOD_HELD} .${CLASS_REF_LINKED}`]: {
                    cursor: 'pointer'
                }
            })}
        >
            <EditorContent editor={editor} />
        </Box>
    );
}
